/**
 * t513u: Edit details (name, sign-in email, phone, job title) for owners and admins, from Settings > Users and the person
 * sheet. t514u: Resend invite for someone whose invite is pending or expired, with a muted "Invited 3 days ago".
 * The rules (checks, what changed, wording) are shared with phone web (@tradeoutlet/shared userEdit.ts); the API is the
 * authority on permissions, uniqueness and the once-a-minute resend limit.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import {
  ApiError, inviteFor, inviteSentToast, invitedAgo, jobTitleEditable, RESEND_INVITE_LABEL, userDetailsPatch, userDetailsProblem, type MemberInvitation, type PersonProfile,
} from '@tradeoutlet/shared';
import { Button, Field, Input, Modal, Spinner } from '@/components/ui';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { invalidateJobTitle } from '@/lib/jobTitle';
import { useCan, usePermissions } from '@/lib/permissions';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';

/** Open invitations (admins only), shared with Settings > Users > Invitations and the person's invite line. */
export function useOpenInvites() {
  const ws = useWorkspaceId();
  const can = useCan('admin.users.manage');
  return useQuery({ queryKey: ['invitations', ws, 'open'], queryFn: () => api.invitations(ws!, 'open'), enabled: Boolean(ws && can) });
}

export function useOpenInvite(userId: string, email?: string | null): MemberInvitation | null {
  return inviteFor(useOpenInvites().data, userId, email);
}

/** Resend invite: a fresh link and expiry to their current email. Toasts "Invite sent to x@y" or the plain reason. */
export function useResendInvite() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [busy, setBusy] = useState(false);
  const send = async (userId: string) => {
    setBusy(true);
    try {
      const r = await api.resendMemberInvite(ws!, userId);
      if (r.outcome === 'emailed') toast(inviteSentToast(r.email), 'success');
      else if (r.outcome === 'added' || r.outcome === 'already_member') toast(`${r.email} already has an account, so they’re in`, 'success');
      else toast(r.message ?? 'The invite wasn’t sent', 'error');
      void queryClient.invalidateQueries({ queryKey: ['invitations', ws] });
      return r;
    } catch (e) {
      toast(errorMessage(e), e instanceof ApiError && e.status === 429 ? 'info' : 'error');
      return null;
    } finally { setBusy(false); }
  };
  return { send, busy };
}

/** The person's invite, as one muted line with Resend invite (nothing when they've joined or weren't invited). */
export function ResendInviteLine({ userId, email, className }: { userId: string; email?: string | null; className?: string }) {
  const inv = useOpenInvite(userId, email);
  const { send, busy } = useResendInvite();
  if (!inv) return null;
  return (
    <div className={`flex flex-wrap items-center gap-2 text-sm ${className ?? ''}`} data-testid="resend-invite-line">
      <span className="min-w-0 flex-1 text-xs text-slate-500 dark:text-slate-400" data-testid="invited-ago">{invitedAgo(inv)}{inv.status === 'expired' ? ' · the link has expired' : ''}</span>
      <Button size="sm" variant="secondary" loading={busy} onClick={() => void send(userId)} data-testid="resend-invite"><Mail className="h-4 w-4" />{RESEND_INVITE_LABEL}</Button>
    </div>
  );
}

/** Invalidate everything that shows a person's name, email or phone. */
export function invalidatePerson(ws: string, userId: string) {
  invalidateJobTitle(ws, userId);
  void queryClient.invalidateQueries({ queryKey: ['invitations', ws] });
}

/** Form state and Save for Edit details, used by the desktop dialog and the phone sheet. */
export function useEditDetails(userId: string, open: boolean) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const mine = usePermissions().data;
  const q = useQuery({ queryKey: ['person', ws, userId], queryFn: () => api.personProfile(ws!, userId), enabled: Boolean(ws && open) });
  const p: PersonProfile | undefined = q.data;
  const [form, setForm] = useState({ display_name: '', email: '', phone: '', job_title: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && p) setForm({ display_name: p.display_name, email: p.email ?? '', phone: p.phone ?? '', job_title: p.job_title ?? '' });
    if (!open) setError(null);
  }, [open, p]);
  const canTitle = p ? jobTitleEditable(p, mine) : false;
  const patch = p ? userDetailsPatch({ display_name: p.display_name, email: p.email, phone: p.phone }, form) : {};
  const title = form.job_title.replace(/\s+/g, ' ').trim() || null;
  const titleChanged = Boolean(p && canTitle && title !== (p.job_title ?? null));
  const dirty = Object.keys(patch).length > 0 || titleChanged;
  const emailChanging = Boolean(patch.email);
  const save = async (): Promise<boolean> => {
    if (!p || !ws) return false;
    const problem = userDetailsProblem({ ...form, had_email: Boolean(p.email) });
    if (problem) { setError(problem); return false; }
    if (!dirty) return true;
    setSaving(true); setError(null);
    try {
      let message = 'Saved';
      if (Object.keys(patch).length) message = (await api.updateMemberDetails(ws, userId, patch)).message;
      if (titleChanged) await api.wf.setJobTitle(ws, userId, title);
      invalidatePerson(ws, userId);
      toast(message, 'success');
      return true;
    } catch (e) { setError(errorMessage(e)); return false; } finally { setSaving(false); }
  };
  return { p, loading: q.isLoading, form, setForm, error, saving, dirty, save, canTitle, emailChanging };
}

/** Desktop: a calm dialog. Save saves and closes; Cancel just closes. */
export function EditUserDialog({ userId, open, onClose, onSaved }: { userId: string; open: boolean; onClose: () => void; onSaved?: () => void }) {
  const e = useEditDetails(userId, open);
  const { form, setForm } = e;
  const submit = async () => { if (await e.save()) { onSaved?.(); onClose(); } };
  return (
    <Modal open={open} onClose={onClose} title={e.p ? `Edit ${e.p.display_name}` : 'Edit details'} footer={<div className="flex w-full flex-wrap items-center gap-2">
      <div className="mr-auto min-w-0">{e.p && <ResendInviteLine userId={userId} email={e.p.email} />}</div>
      <Button variant="ghost" onClick={onClose} data-testid="edit-user-cancel">Cancel</Button>
      <Button onClick={() => void submit()} loading={e.saving} disabled={!e.p} data-testid="edit-user-save">Save</Button>
    </div>}>
      {!e.p ? <Spinner /> : (
        <form className="space-y-3" data-testid="edit-user" onSubmit={ev => { ev.preventDefault(); void submit(); }}>
          <Field label="Name"><Input value={form.display_name} maxLength={120} autoComplete="off" onChange={ev => setForm({ ...form, display_name: ev.target.value })} data-testid="edit-user-name" /></Field>
          <Field label="Email" note={e.emailChanging ? 'This is how they sign in. We’ll tell them at their old address, and resend an open invite to the new one.' : 'This is how they sign in.'}>
            <Input type="email" value={form.email} maxLength={320} autoComplete="off" onChange={ev => setForm({ ...form, email: ev.target.value })} data-testid="edit-user-email" />
          </Field>
          <Field label="Phone"><Input type="tel" value={form.phone} maxLength={40} autoComplete="off" onChange={ev => setForm({ ...form, phone: ev.target.value })} data-testid="edit-user-phone" /></Field>
          {e.canTitle && <Field label="Job title"><Input value={form.job_title} maxLength={120} autoComplete="off" onChange={ev => setForm({ ...form, job_title: ev.target.value })} data-testid="edit-user-job-title" /></Field>}
          {e.p.can.view_hr && <p className="text-xs text-slate-500 dark:text-slate-400">Preferred and legal names are in their Personal details.</p>}
          {e.error && <p className="text-sm text-rose-700 dark:text-rose-300" role="alert" data-testid="edit-user-error">{e.error}</p>}
          <button type="submit" hidden />
        </form>
      )}
    </Modal>
  );
}
